/**
 * useAudioPlayer Hook
 * Handles base64 audio decoding and playback for TTS
 * Optimized for Safari/Mobile: reuses a single audio element and primes it on user gesture
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAudioPlayerReturn {
  /** Play audio from base64 string */
  playAudio: (base64Audio: string, format?: string, allowAutoplay?: boolean) => Promise<void>;
  /** Is audio currently playing */
  isPlaying: boolean;
  /** Audio playback error */
  error: string | null;
  /** Stop current audio */
  stop: () => void;
}

export function useAudioPlayer(): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasUserInteractedRef = useRef(false);
  const currentBlobUrlRef = useRef<string | null>(null);

  // Initialize the audio element once
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      const audio = audioRef.current;
      
      // Critical Safari settings
      audio.preload = 'auto';
      
      audio.onplay = () => {
        setIsPlaying(true);
      };

      audio.onended = () => {
        setIsPlaying(false);
      };

      audio.onerror = (e) => {
        console.error('[useAudioPlayer] Audio element error:', e);
        setIsPlaying(false);
      };
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
      }
    };
  }, []);

  // Track user interaction to allow audio autoplay
  useEffect(() => {
    const handleUserInteraction = () => {
      if (hasUserInteractedRef.current) return;
      hasUserInteractedRef.current = true;
      
      console.log('[useAudioPlayer] User interaction detected, priming audio engine');
      if (audioRef.current) {
        // Prime with a 0.1s silent wav to unlock the audio context/element
        audioRef.current.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
        audioRef.current.load();
        audioRef.current.play().catch(() => {});
      }
    };

    document.addEventListener('click', handleUserInteraction, { capture: true, once: true });
    document.addEventListener('touchstart', handleUserInteraction, { capture: true, once: true });
    document.addEventListener('keydown', handleUserInteraction, { capture: true, once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  const base64ToBlob = useCallback((base64: string, mimeType: string): string => {
    try {
      const base64Data = base64.replace(/^data:audio\/\w+;base64,/, '');
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('[useAudioPlayer] Failed to convert base64 to blob:', err);
      throw new Error('Failed to decode audio data');
    }
  }, []);

  const playAudio = useCallback(
    async (base64Audio: string, format: string = 'wav', allowAutoplay: boolean = false) => {
      if (allowAutoplay && !hasUserInteractedRef.current) {
        console.warn('[useAudioPlayer] Autoplay blocked: No user interaction yet');
        return;
      }

      if (!audioRef.current) return;

      setError(null);

      try {
        // 1. Cleanup previous
        audioRef.current.pause();
        if (currentBlobUrlRef.current) {
          URL.revokeObjectURL(currentBlobUrlRef.current);
        }

        // 2. Prepare new
        const mimeType = format === 'mp3' ? 'audio/mpeg' : 'audio/wav';
        const blobUrl = base64ToBlob(base64Audio, mimeType);
        currentBlobUrlRef.current = blobUrl;

        // 3. Load and Play
        audioRef.current.src = blobUrl;
        audioRef.current.load(); // Vital for Safari when changing src
        
        await audioRef.current.play();
      } catch (err) {
        if (err instanceof Error && err.name === 'NotAllowedError') {
          console.error('[useAudioPlayer] Playback blocked by browser policy');
          return;
        }
        
        console.error('[useAudioPlayer] Playback failed:', err);
        setError(err instanceof Error ? err.message : 'Playback failed');
        setIsPlaying(false);
      }
    },
    [base64ToBlob]
  );

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  return { playAudio, isPlaying, error, stop };
}
